import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { ApiError } from '@/shared/api/api-client';
import type { UnblockSlotRequest } from '@/types/block-availability-api';
import { invalidateAfterUnblock, postUnblockSlotTo } from './use-unblock-slot';

/**
 * `POST /api/Scheduling/UnblockResourceSlot` — the DEVICE unblock route
 * (PROD-2510, Scheduling `8722bf64`), the twin of `useUnblockSlot`.
 *
 * Same three-key body (`{ id, practiceId, recurringId }`), same
 * `recurringId` semantics — null unblocks this occurrence, the series id
 * unblocks this and all following. `UnblockResourceSlotCommand` derives from
 * `UnblockSlotCommand` and adds no wire field; its one extra property is
 * server-set and never read from the request.
 *
 * MIRRORING BOTH HALVES WAS THE POINT, not tidiness. Unblock builds the same
 * SignalR broadcast block does and omits the participant entirely, so nothing
 * in the payload says which calendar column the removed tile belonged to. On
 * the practitioner calendar that has always been survivable; on the device
 * calendar, which is keyed by device, it leaves the block tile behind.
 * Mirroring only the block half would have moved the bug rather than fixed it,
 * so this route names the device on the broadcast and the provider route is
 * left exactly as it was.
 *
 * Both routes refuse the other actor kind, so the choice is load-bearing: a
 * device-owned slot sent to `useUnblockSlot` is a 400, not a silent success.
 * `BlockAvailabilityPanel` picks from `state.actorKind`, the same discriminant
 * that picked the route on the way in — which matters here because unblock is
 * reached from the EDIT view, where the panel was opened by clicking a tile on
 * a device column rather than by choosing an actor.
 *
 * Verified live on dev 2026-09-23 against a throwaway device block.
 */
export function useUnblockResourceSlot() {
  const qc = useQueryClient();
  return useMutation<void, ApiError, UnblockSlotRequest>({
    mutationKey: ['unblock-resource-slot'],
    mutationFn: (body) => postUnblockSlotTo('/api/Scheduling/UnblockResourceSlot', body),
    onSuccess: (_data, vars) => invalidateAfterUnblock(qc, vars.id),
  });
}
